import { defineStore } from 'pinia';
import { useProductsStore } from './products.store';
import { RouteLocationRaw } from 'vue-router';

import ImageGetDga from '@/assets/images/get_dga.png';
import ImageGetBank from '@/assets/images/get_bank.png';
import ImageGetContracts from '@/assets/images/get_contracts.png';
import ImageGetAccountant from '@/assets/images/get_accountant.png';
import ImageGetZebraLegal from '@/assets/images/get_zebra_legal.png';
import ImageGetTrademark from '@/assets/images/get_trademark.png';
import GoDutchBanner from '@/assets/images/jobs/banners/Go Dutch.png';
import BookkeeperBanner from '@/assets/images/jobs/banners/AirBill.jpg';
import MollieBanner from '@/assets/images/jobs/banners/Mollie.png';
import GoDutchIcon from '@/assets/images/jobs/icons/GoDutch.png';
import MollieIcon from '@/assets/images/jobs/icons/Mollie.png';
import RevolutBanner from '@/assets/images/banking/revolut-card.png';
import RevolutIcon from '@/assets/images/banking/revolut-icon.svg';
import ReconiBanner from '@/assets/images/jobs/banners/Reconi.png';
import ReconiIcon from '@/assets/images/jobs/icons/Reconi.png';
import BookkeeperIcon from '@/assets/images/jobs/icons/AirBill.png';
import AccountingIcon from '@/assets/images/jobs/icons/Accounting.png';
import IncorporateIcon from '@/assets/images/jobs/icons/Incorporate.png';
import HoldingIcon from '@/assets/images/jobs/icons/Holding.png';
import ModifyIcon from '@/assets/images/jobs/icons/Modify.png';
import DgaSalaryIcon from '@/assets/images/jobs/icons/DGA Salary.png';
import BankingIcon from '@/assets/images/jobs/icons/Banking.png';
import ContractsIcon from '@/assets/images/jobs/icons/Contracts.png';
import { computedAsync } from '@vueuse/core';
import { apiClient } from '../api';
import { UserRoleEnum } from '@/enums/global.enum';
import { useUserStore } from '@/stores/user.store';
import { useApplicationStore } from '@/stores/application.store';
import { reportAsyncError } from '@/core/services/error-reporting';
import { isFeatureFlagEnabled, usePostHog } from '@/composables/usePostHog';
// From the leaf module, not the `usePostHog` re-export: the offer definitions below read this
// enum at module scope, where a spec's in-flight mock of `usePostHog` would hand back `undefined`.
import { PosthogFeatureFlags } from '@/composables/posthog-feature-flags';

export type OfferCategory = 'recommended' | 'incorporation' | 'modify' | 'accounting' | 'finances' | 'compliance' | 'specialist';

/**
 * Canonical order of the browsable product categories (the tabs in AllProductsGrid).
 * Deliberately excludes 'recommended', which feeds the recommendations strip, not a tab.
 */
const CATEGORY_ORDER: OfferCategory[] = ['incorporation', 'modify', 'accounting', 'finances', 'compliance', 'specialist'];

export enum OfferRecommendationRule {
    HaveNoJobs = 'have_no_jobs',
    HaveJobs = 'have_jobs',
}

export type Offer = {
    categories: OfferCategory[];
    recommendWhen: OfferRecommendationRule[];
    title: string;
    description: string;
    icon?: string;
    banner?: string;
    price: number;
    deal?: string; // Free | First 3 month free | 10% off
    route: RouteLocationRaw;
    externalUrl?: string;
    visibleTo?: UserRoleEnum[];
    name?: OfferName;
};

export enum OfferName {
    AirBill = 'airbill',
    Banking = 'banking',
    GoDutch = 'godutch',
    Mollie = 'mollie',
    Revolut = 'revolut',
}

export const useOffersStore = defineStore('offers', () => {
    const productsStore = useProductsStore();
    const userStore = useUserStore();
    const applicationStore = useApplicationStore();
    const { locale } = useI18n();

    // Registers the flag mirror's `onFeatureFlags` subscription, so `allOffers` re-evaluates once
    // the flags arrive rather than settling on whatever was known when the store was first read.
    usePostHog();

    const lang = computed(() => locale.value.split('-').at(0) ?? 'nl');

    /**
     * `null` means "not known yet" — `recommended` shows nothing until it resolves.
     *
     * The lookup is skipped for anonymous visitors: the endpoint needs a session and 401s
     * without one, and the funnel start page (`/start`) mounts this store before anybody has
     * logged in. Reading `userStore.user` synchronously, before the first `await`, is what
     * registers it as a dependency — the lookup then runs by itself once a session arrives.
     *
     * Anonymous visitors stay at `null` rather than `false`: they are not "a user with no
     * jobs", and recommendations are not meant for them.
     */
    const hasJobs = computedAsync(
        async () => {
            if (!userStore.user) return null;

            const pendingJobs = await apiClient.processes.$get([
                { key: 'limit', value: 1 },
                { key: 'state', value: 'pending' },
                { key: 'scenario_type', value: 'incorporation' },
                { key: 'paid', value: 'true' },
            ]);

            return pendingJobs.total > 0;
        },
        null,
        // Without this, @vueuse/core reports the rejected payload through `globalThis.reportError`,
        // which never reaches Vue's `errorHandler` and lands in Rollbar as `(unknown): [object Object]`.
        { onError: reportAsyncError('offers.hasJobs') },
    );

    const toOffer = (original: OfferDefinition, products: typeof productsStore.products): Offer => {
        const swap = original.whenFlagOn && isFeatureFlagEnabled(original.whenFlagOn.flag) ? original.whenFlagOn : undefined;
        const definition = swap ? { ...original, route: swap.route, product: swap.product } : original;

        let price: number = definition.price?.[lang.value] ?? 0;

        if (definition.product) {
            price = definition.product.reduce((sum, productReference) => {
                const product = products[productReference];
                return sum + (product?.price ?? 0);
            }, 0);
        }

        return {
            categories: definition.category,
            recommendWhen: definition.recommendWhen ?? [],
            title: definition.title[lang.value],
            description: definition.description[lang.value],
            icon: definition.icon,
            banner: definition.banner,
            price: price,
            deal: definition.deal?.[lang.value],
            route: definition.route,
            externalUrl: definition.externalUrl,
            visibleTo: definition.visibleTo,
            name: definition.name,
        } satisfies Offer;
    };

    const allOffers = computed<Offer[]>(() => {
        const products = productsStore.products;

        if (Object.keys(products).length === 0) return [];

        const accountType = userStore.type;
        const isBookkeeperTenant = !!applicationStore.info?.tenant?.isBookkeeper;

        return offerDefinitions
            .filter((definition) => !definition.visibleTo || definition.visibleTo.includes(accountType))
            .filter((definition) => !(isBookkeeperTenant && definition.hiddenOnBookkeeperTenant))
            .filter((definition) => !definition.featureFlag || isFeatureFlagEnabled(definition.featureFlag))
            .filter((definition) => !definition.unlisted)
            .map((definition) => toOffer(definition, products));
    });

    /**
     * One named offer, **ignoring the catalogue filters** (`visibleTo`, `hiddenOnBookkeeperTenant`,
     * `featureFlag`, `unlisted`). For a surface that has already decided which offer to show by its own rule —
     * the **Banking offer** picks its bank from the banking-provider flag, not from `upsell-mollie` —
     * and that a support user viewing the client's process should see exactly as the client does.
     *
     * Reactive when called inside a `computed`; `undefined` until products have loaded, as in `allOffers`.
     */
    const offerByName = (name: OfferName): Offer | undefined => {
        const products = productsStore.products;
        if (Object.keys(products).length === 0) return undefined;

        const definition = offerDefinitions.find((d) => d.name === name);
        return definition && toOffer(definition, products);
    };

    /**
     * Based on user have active jobs or not (Has entity or not) we show different recommendations.
     * When user HAVE jobs we offer banking, contracts, etc.
     * When user HAVE NO jobs we offer 'incorporation', 'holding', etc.
     */
    const recommended = computed(() => {
        const _hasJobs = hasJobs.value;
        if (_hasJobs === null) return [];

        return allOffers.value.filter((offer) => {
            return offer.recommendWhen.includes(_hasJobs ? OfferRecommendationRule.HaveJobs : OfferRecommendationRule.HaveNoJobs);
        });
    });

    /**
     * The browsable categories that currently have at least one active offer
     * (after the visibleTo account-type filter), in canonical tab order.
     */
    const categories = computed<OfferCategory[]>(() => {
        const active = new Set(allOffers.value.flatMap((offer) => offer.categories));
        return CATEGORY_ORDER.filter((category) => active.has(category));
    });

    return { allOffers, offerByName, recommended, categories };
});

type OfferDefinition = {
    category: OfferCategory[];
    recommendWhen?: OfferRecommendationRule[];
    title: { nl: string; en: string };
    description: { nl: string; en: string };
    banner?: string;
    logo?: string;
    icon?: string;
    route: RouteLocationRaw;
    externalUrl?: string;
    price?: {
        nl: number;
        en: number;
        intake?: boolean;
        perMonth?: boolean;
    };
    deal?: { nl: string; en: string };
    product?: string[];
    /**
     * Optional allowlist of account types that may see this offer.
     * Omitted → visible to everyone (default). A non-empty list restricts the offer
     * to exactly those account types; the current viewer's account type must be in it
     * (no support/notary bypass). An empty [] therefore hides the offer from everyone.
     */
    visibleTo?: UserRoleEnum[];
    /**
     * Marks a **tenant-provided service**: bookkeeping or DGA salary administration, which a
     * bookkeeper-owned tenant (`tenant.isBookkeeper`) performs for its own clients. Such an offer is
     * dropped everywhere `allOffers` feeds on those tenants. Omitted → not affected.
     *
     * A separate axis from `visibleTo`: that one is about who is logged in, this one about who owns
     * the tenant. Deliberately explicit rather than derived from category or route — the offers it
     * covers are spread over `finances`, `recommended` and `accounting`. See CONTEXT.md.
     */
    hiddenOnBookkeeperTenant?: boolean;
    /**
     * Kept out of the catalogue (`allOffers`) but still reachable through `offerByName`. For the
     * single banks, which the catalogue now reaches through the one Banking offer and its picker
     * page, while the post-incorporation **Banking offer** still shows one bank's card by name.
     */
    unlisted?: boolean;
    name?: OfferName;
    /**
     * Optional PostHog flag gating this offer. Omitted → always shown (default). Present → the
     * offer is dropped everywhere `allOffers` feeds (shop deals, the products grid,
     * recommendations) unless the flag is on for this visitor.
     *
     * A flag that does not exist in the project the build points at reads as off, so a gated offer
     * stays hidden until somebody creates it. That is the intended failure mode: a missing flag
     * hides the offer rather than showing it to everyone.
     */
    featureFlag?: PosthogFeatureFlags;
    /**
     * Optional PostHog flag that swaps where this offer leads, not whether it is shown. While the
     * flag is on, `route` and `product` (and so the price) are replaced by the ones given here.
     *
     * Off, missing, or not loaded yet → the offer keeps its own route and product. Only this card
     * changes: other links to the default route are untouched. See docs/adr/0032.
     */
    whenFlagOn?: {
        flag: PosthogFeatureFlags;
        route: RouteLocationRaw;
        product: string[];
    };
};

/**
 * The new share-transfer funnel, for the offers that sell a share transfer. See docs/adr/0032.
 * The route is its entry page: the scope chosen there starts the funnel or goes to the general workflow.
 */
const shareTransferFunnel: OfferDefinition['whenFlagOn'] = {
    flag: PosthogFeatureFlags.ShareTransferFunnel,
    route: { name: 'share-transfer' },
    product: ['share-transfer-internal'],
};

const offerDefinitions: OfferDefinition[] = [
    // recommended
    {
        category: ['finances'],
        recommendWhen: [OfferRecommendationRule.HaveJobs],
        visibleTo: [UserRoleEnum.Client],
        hiddenOnBookkeeperTenant: true,
        title: {
            en: 'AirBill',
            nl: 'AirBill',
        },
        description: {
            en: 'AI processes your bookkeeping. The accountants at UWBS review it and handle your tax returns.',
            nl: 'AI verwerkt je administratie. De accountants van UWBS controleren en verzorgen je aangiften.',
        },
        banner: BookkeeperBanner,
        icon: BookkeeperIcon,
        deal: {
            en: 'First 2 months free',
            nl: 'Eerste 2 maanden gratis',
        },
        route: { path: '/dashboard/client/bookkeeping' },
    },
    // {
    //     category: ['finances'],
    //     name: OfferName.AirBill,
    //     recommendWhen: [OfferRecommendationRule.HaveJobs],
    //     visibleTo: [UserRoleEnum.Client],
    //     title: {
    //         en: 'AirBill',
    //         nl: 'AirBill',
    //     },
    //     description: {
    //         en: 'AI powered accounting software',
    //         nl: 'Boekhoudsoftware op basis van AI',
    //     },
    //     banner: AirBillBanner,
    //     icon: AirBillIcon,
    //     deal: {
    //         en: 'Free',
    //         nl: 'Gratis',
    //     },
    //     route: { path: '/start/product/airbill' },
    // },
    {
        category: ['finances'],
        name: OfferName.Banking,
        recommendWhen: [OfferRecommendationRule.HaveJobs],
        title: {
            en: 'Business Bank Account',
            nl: 'Zakelijke bankrekening',
        },
        description: {
            en: 'Compare Mollie, Revolut and GoDutch',
            nl: 'Vergelijk Mollie, Revolut en GoDutch',
        },
        banner: ImageGetBank,
        icon: BankingIcon,
        route: { name: 'start-banking' },
        deal: {
            en: '€100 cashback',
            nl: '€100 cashback',
        },
    },
    {
        category: ['finances'],
        name: OfferName.GoDutch,
        unlisted: true,
        title: {
            en: 'GoDutch',
            nl: 'GoDutch',
        },
        description: {
            en: 'All-in-one business account',
            nl: 'Alles-in-één zakelijke rekening',
        },
        banner: GoDutchBanner,
        icon: GoDutchIcon,
        route: { path: '/start/product/godutch' },
        deal: {
            en: '€100 from Firm24 deposited',
            nl: '€100 van Firm24 gestort',
        },
    },
    {
        category: ['finances'],
        name: OfferName.Mollie,
        unlisted: true,
        title: {
            en: 'Mollie',
            nl: 'Mollie',
        },
        description: {
            en: 'Business account and online payments in one',
            nl: 'Zakelijke rekening én online betalingen in één',
        },
        banner: MollieBanner,
        icon: MollieIcon,
        route: { path: '/start/product/mollie' },
        deal: {
            en: '€100 cashback',
            nl: '€100 cashback',
        },
    },
    {
        category: ['finances'],
        name: OfferName.Revolut,
        unlisted: true,
        title: {
            en: 'Revolut',
            nl: 'Revolut',
        },
        description: {
            en: 'Business account for doing business abroad',
            nl: 'Zakelijke rekening voor zakendoen in het buitenland',
        },
        banner: RevolutBanner,
        icon: RevolutIcon,
        route: { path: '/start/product/revolut' },
    },
    {
        category: ['compliance'],
        visibleTo: [UserRoleEnum.Client],
        title: {
            en: 'Reconi eHerkenning',
            nl: 'Reconi eHerkenning',
        },
        description: {
            en: 'eHerkenning level 3 for your business',
            nl: 'eHerkenning niveau 3 voor uw bedrijf',
        },
        banner: ReconiBanner,
        icon: ReconiIcon,
        route: { path: '/start/product/reconi' },
        deal: {
            en: '10% discount',
            nl: '10% korting',
        },
    },

    {
        category: ['recommended'],
        title: {
            en: 'Get a Dutch bank account within 1 business day',
            nl: 'Binnen 1 werkdag een Nederlandse bankrekening openen',
        },
        description: {
            en: 'A Dutch IBAN plus a virtual Mastercard ready for use within 1 business day.',
            nl: 'Een Nederlandse IBAN plus een virtuele Mastercard, binnen 1 werkdag klaar voor gebruik.',
        },
        banner: ImageGetBank,
        icon: BankingIcon,
        route: { name: 'client-banking' },
        price: { nl: 0, en: 0 },
    },
    {
        category: ['recommended'],
        title: {
            en: 'Get all the contracts you need to protect your BV',
            nl: 'Ontvang alle contracten die u nodig hebt om uw BV te beschermen',
        },
        description: {
            en: 'Ensure 100% compliance and avoid issues with the tax authorities.',
            nl: 'Zorg voor 100% naleving en voorkom problemen met de belastingdienst.',
        },
        banner: ImageGetContracts,
        icon: ContractsIcon,
        route: { name: 'client-contracts' },
        price: { nl: 0, en: 0 },
    },
    {
        visibleTo: [UserRoleEnum.Client],
        hiddenOnBookkeeperTenant: true,
        category: ['recommended'],
        title: {
            en: "Get a free quote from {name}'s trusted accountants",
            nl: 'Ontvang een gratis offerte van de vertrouwde accountants van {name}',
        },
        description: {
            en: "Save dozens of hours from finding an accountant. No obligation to work with them if you don't like them.",
            nl: 'Bespaar tientallen uren van het vinden van een accountant. Geen verplichting om met ze te werken als je ze niet leuk vindt.',
        },
        banner: ImageGetAccountant,
        icon: AccountingIcon,
        route: { name: 'client-bookkeeping' },
        price: { nl: 0, en: 0 },
    },
    {
        visibleTo: [UserRoleEnum.Client],
        hiddenOnBookkeeperTenant: true,
        category: ['recommended'],
        title: {
            en: 'Arrange your monthly DGA salary administration',
            nl: 'Regel je maandelijkse DGA-salarisadministratie',
        },
        description: {
            en: 'We will arrange your monthly payroll declaration and submit it to the tax authorities.',
            nl: 'Wij verzorgen je maandelijkse loonaangifte en dienen deze in bij de Belastingdienst.',
        },
        banner: ImageGetDga,
        icon: DgaSalaryIcon,
        route: { name: 'client-dga-salary-admin' },
        price: { nl: 0, en: 0 },
    },
    {
        category: ['recommended'],
        title: {
            en: 'Have your documents custom-tailored',
            nl: 'Laat je documenten op maat opstellen',
        },
        description: {
            en: 'All legal documents for the growth phase of your startup',
            nl: 'Alle juridische documenten voor de groeifase van je startup',
        },
        banner: ImageGetZebraLegal,
        icon: ContractsIcon,
        route: { name: 'client-zebra-legal' },
        price: { nl: 0, en: 0 },
    },
    {
        category: ['recommended'],
        title: {
            en: 'Secure your brand name through trademark registration',
            nl: 'Leg je merknaam vast via een merkregistratie',
        },
        description: {
            en: 'Prevent others from copying your brand. Schedule a free consultation with a trademark specialist.',
            nl: 'Voorkom dat anderen je merk gaan kopiëren. Plan een gratis adviesgesprek met een merkspecialist in.',
        },
        banner: ImageGetTrademark,
        icon: ContractsIcon,
        // `/jobs?job=consultation-ip-advisor` redirects here (see jobs.router.ts)
        route: { name: 'start-general-flow', query: { job: 'consultation-ip-advisor' } },
        price: { nl: 0, en: 0 },
    },

    // incorporate
    {
        category: ['incorporation'],
        recommendWhen: [OfferRecommendationRule.HaveNoJobs],
        title: {
            en: 'Incorporate your Dutch BV',
            nl: 'BV oprichten',
        },
        description: {
            en: 'Set up your BV online, from start to finish. Fast and easy in just 3 steps.',
            nl: 'Vanuit huis je BV oprichten van begin tot eind. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['incorporation-bv'],
        route: { name: 'start-incorporation', query: { product: 'incorporation-bv', scenario: 'incorporation-bv' } },
        icon: IncorporateIcon,
    },
    {
        category: ['incorporation'],
        recommendWhen: [OfferRecommendationRule.HaveNoJobs],
        title: {
            en: 'Incorporate your Holding',
            nl: 'Holding oprichten',
        },
        description: {
            en: 'Set up your holding company online, from start to finish. Quick and easy in just 3 steps.',
            nl: 'Vanuit huis je Holding oprichten van begin tot eind. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['incorporation-holding'],
        route: { name: 'start-incorporation', query: { product: 'incorporation-holding', scenario: 'incorporation-bv' } },
        icon: HoldingIcon,
    },
    {
        category: ['incorporation'],
        title: {
            en: 'Incorporate Savings BV',
            nl: 'Spaar BV oprichten',
        },
        description: {
            en: 'Easily set up a Spaar BV with Firm24 to save on taxes for large savings.',
            nl: 'Vanuit huis je Spaar BV oprichten. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['incorporation-bv', 'upsell-high-share-capital'],
        route: { name: 'start-incorporation', query: { product: 'incorporation-savings-bv', scenario: 'incorporation-bv' } },
        icon: IncorporateIcon,
    },
    {
        category: ['incorporation'],
        title: {
            en: 'Holding BV structure',
            nl: 'Holding BV structuur',
        },
        description: {
            en: 'Incorporate a new operating BV and a holding company.',
            nl: 'Een nieuwe BV en holding oprichten.',
        },
        product: ['incorporation-bv', 'upsell-holding'],
        route: { name: 'start-incorporation', query: { product: 'holding-bv-structure', scenario: 'incorporation-bv' } },
        icon: HoldingIcon,
    },
    {
        category: ['incorporation'],
        title: {
            en: 'Silent contribution of sole proprietorship',
            nl: 'Geruisloze inbreng in een BV',
        },
        description: {
            en: 'Silent contribution of sole proprietorship in BV in 3 simple steps. Tax guidance required (RA/tax advisor).',
            nl: 'Regel jouw geruisloze inbreng met ons gestroomlijnde online proces. Fiscale begeleiding vereist (RA/fiscalist).',
        },
        product: ['incorporation-bv', 'incorporation-bv-deed-of-transfer'],
        route: { name: 'start-incorporation', query: { product: 'incorporation-bv-deed-of-transfer', scenario: 'transfer-bv' } },
        icon: IncorporateIcon,
    },
    {
        category: ['incorporation'],
        title: {
            en: 'Assets-liabilities transaction to the BV',
            nl: 'Ruisende inbreng in een BV',
        },
        description: {
            en: 'Assets-liabilities transaction of sole proprietorship to the BV. Tax guidance required (RA/tax advisor).',
            nl: 'Regel jouw ruisende inbreng met ons gestroomlijnde online proces. Fiscale begeleiding vereist (RA/fiscalist)',
        },
        product: ['incorporation-bv', 'incorporation-bv-deed-of-transfer'],
        route: { name: 'start-incorporation', query: { product: 'incorporation-bv-deed-of-transfer', scenario: 'transfer-bv' } },
        icon: IncorporateIcon,
    },
    {
        category: ['incorporation'],
        title: {
            en: 'Convert sole proprietorship into BV',
            nl: 'Eenmanszaak omzetten naar BV',
        },
        description: {
            en: 'Convert your sole proprietorship into a BV through an asset-liability transaction. Do it easily online!',
            nl: 'Je eenmanszaak omzetten via een activa passiva transactie? Dat kan gemakkelijk online.',
        },
        product: ['incorporation-bv'],
        route: { name: 'start-incorporation', query: { product: 'incorporation-bv-convert-activa-passiva', scenario: 'convert-bv' } },
        icon: IncorporateIcon,
    },
    {
        category: ['incorporation'],
        title: {
            en: 'Convert VOF into BV',
            nl: 'Vof omzetten naar BV',
        },
        description: {
            en: 'Convert your VOF. Fast and easy online in 3 steps.',
            nl: 'Je VOF omzetten via een activa passiva transactie? Dat kan gemakkelijk online.',
        },
        product: ['incorporation-bv'],
        route: { name: 'start-incorporation', query: { product: 'incorporation-bv-convert-activa-passiva', scenario: 'convert-bv' } },
        icon: IncorporateIcon,
    },
    {
        category: ['incorporation'],
        title: {
            en: 'Incorporate your STAK',
            nl: 'STAK oprichten',
        },
        description: {
            en: 'Easily establish a Stichting Administratiekantoor (STAK) with Firm24 to separate voting and profit rights.',
            nl: 'Vanuit huis je STAK oprichten. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['oprichting-stak'],
        route: { name: 'start-general-flow', query: { job: 'incorporation-stak' } },
        icon: IncorporateIcon,
    },
    {
        category: ['incorporation'],
        title: {
            en: 'Tax advice on changing your legal entity',
            nl: 'Fiscaal advies omzetting',
        },
        description: {
            en: 'Do you have a sole proprietorship or general partnership? Determine the appropriate conversion method and discuss it with a tax specialist.',
            nl: 'Heb je een eenmanszaak of VOF? Bepaal de geschikte omzettingsmethode en overleg dit met een fiscalist.',
        },
        price: { en: 249, nl: 249 },
        route: { name: 'start-general-flow', query: { job: 'consultation-tax-lawyer' } },
        icon: IncorporateIcon,
    },
    {
        category: ['incorporation'],
        title: {
            en: 'Tax specialist',
            nl: 'Fiscale begeleiding',
        },
        description: {
            en: 'Have your tax documents drawn up directly by a tax specialist.',
            nl: 'Laat de fiscale stukken direct opstellen door een fiscalist.',
        },
        price: { en: 1700, nl: 1700 },
        route: { name: 'start-general-flow', query: { job: 'consultation-tax-lawyer' } },
        icon: IncorporateIcon,
    },
    {
        category: ['incorporation'],
        title: {
            en: 'Incorporate your cooperative',
            nl: 'Coöperatie oprichten',
        },
        description: {
            en: 'Set up your cooperative with a streamlined process and personal guidance from our experts.',
            nl: 'Vanuit huis je coöperatie oprichten. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['oprichting-coöperatie'],
        route: { name: 'start-general-flow', query: { job: 'incorporation-cooperative' } },
        icon: IncorporateIcon,
    },
    {
        category: ['incorporation'],
        title: {
            en: 'Incorporate your Dutch BV from abroad',
            nl: 'BV oprichten vanuit het buitenland',
        },
        description: {
            en: 'Incorporate your Dutch BV with foreign shareholder from abroad. Fast, easy, and fully online in just 3 steps.',
            nl: 'BV met buitenlandse aandeelhouder oprichten vanuit het buitenland. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['oprichting-bv-met-buitenlandse-aandeelhouder'],
        route: { name: 'start-general-flow', query: { job: 'incorporation-bv-with-foreign-shareholder' } },
        icon: IncorporateIcon,
    },
    {
        category: ['incorporation'],
        title: {
            en: 'Incorporate your Dutch BV company from abroad',
            nl: 'BV oprichten vanuit het buitenland',
        },
        description: {
            en: 'Incorporate your Dutch BV with foreign entity from abroad. Fast, easy, and fully online in just 3 steps.',
            nl: 'BV met buitenlandse entiteit oprichten vanuit het buitenland. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['oprichting-bv-door-buitenlandse-entiteit'],
        route: { name: 'start-general-flow', query: { job: 'incorporation-bv-by-foreign-entity' } },
        icon: IncorporateIcon,
    },

    // modify
    {
        category: ['modify'],
        title: {
            en: 'Share transfer',
            nl: 'Aandelenoverdracht',
        },
        description: {
            en: 'Transfer your company shares from home. Fast and easy in 3 simple steps.',
            nl: 'Vanuit huis je aandelenoverdracht regelen. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['share-transfer-external'],
        route: { name: 'start-general-flow', query: { job: 'share-transfer' } },
        whenFlagOn: shareTransferFunnel,
        icon: ModifyIcon,
    },
    {
        category: ['modify'],
        title: {
            en: 'Articles of association',
            nl: 'Statutenwijziging',
        },
        description: {
            en: 'Amend your articles of association online. Our legal experts guide you through every step.',
            nl: 'Vanuit huis je Statutenwijziging regelen. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['statutenwijziging'],
        route: { name: 'start-general-flow', query: { job: 'amendment-of-the-articles-of-association' } },
        icon: ModifyIcon,
    },
    {
        category: ['modify'],
        title: {
            en: 'Share merger',
            nl: 'Aandelenfusie',
        },
        description: {
            en: 'Complete your share merger from home. Fast and easy in 3 online steps.',
            nl: 'Vanuit huis je aandelenfusie regelen. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['aandelenfusie'],
        route: { name: 'start-general-flow', query: { job: 'share-merger' } },
        icon: ModifyIcon,
    },
    {
        category: ['modify'],
        title: {
            en: 'Share issuance',
            nl: 'Uitgifte aandelen',
        },
        description: {
            en: 'Issue new company shares from home. Fast and easy in 3 online steps.',
            nl: 'Vanuit huis je aandelen uitgifte regelen. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['uitgifte-aandelen'],
        route: { name: 'start-general-flow', query: { job: 'issuance-of-shares' } },
        icon: ModifyIcon,
    },
    {
        category: ['modify'],
        title: {
            en: 'Upgrade BV to a holding structure',
            nl: 'BV upgraden naar een holdingstructuur',
        },
        description: {
            en: 'Convert your BV into a holding structure easily online, with full legal guidance.',
            nl: 'Vanuit huis je BV upgraden naar een holdingstructuur. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['share-transfer-external'],
        route: { name: 'start-general-flow', query: { job: 'share-transfer' } },
        whenFlagOn: shareTransferFunnel,
        icon: HoldingIcon,
    },
    {
        category: ['modify'],
        title: {
            en: 'Dissolve your BV',
            nl: 'BV opheffen',
        },
        description: {
            en: 'With FIRM24, dissolving your BV is easy and worry-free. Our legal experts will guide you from start to finish.',
            nl: 'Vanuit huis je BV opheffen. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['liquidation'],
        route: { name: 'start-liquidation' },
        icon: ModifyIcon,
    },
    {
        category: ['modify'],
        title: {
            en: 'File for your bankruptcy',
            nl: 'Faillissement aanvragen',
        },
        description: {
            en: 'Facing bankruptcy can be overwhelming. Call in an expert to guide you through every critical step.',
            nl: 'Vanuit huis je Faillisement aanvragen. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['faillissement-eigen-bedrijf-aanvragen'],
        route: { name: 'start-general-flow', query: { job: 'application-bankruptcy-own-company' } },
        icon: ModifyIcon,
    },

    // accounting
    {
        visibleTo: [UserRoleEnum.Client],
        hiddenOnBookkeeperTenant: true,
        category: ['accounting'],
        title: {
            en: 'Easily find an accountant for DGA salary administration',
            nl: 'Vind een boekhouder voor de DGA salarisadministratie',
        },
        description: {
            en: 'We will arrange your monthly payroll declaration and submit it to the tax authorities.',
            nl: 'Kom eenvoudig in contact met gekwalificeerde boekhouders voor de DGA salarisadministratie.',
        },
        product: ['upsell-dga-salary'],
        route: { name: 'client-dga-salary-admin' },
        icon: DgaSalaryIcon,
    },
    {
        visibleTo: [UserRoleEnum.Client],
        hiddenOnBookkeeperTenant: true,
        category: ['accounting'],
        title: {
            en: 'Bookkeeping',
            nl: 'Boekhouding',
        },
        description: {
            en: 'Your complete bookkeeping taken care of by our specialists. Personal contact and tailored advice. Quality service at a competitive rate.',
            nl: 'Je volledige boekhouding eenvoudig geregeld door onze specialisten. Persoonlijk contact en advies op maat. Kwaliteit tegen een scherp tarief.',
        },
        price: { en: 149, nl: 149, perMonth: true },
        route: { name: 'client-bookkeeping' },
        icon: AccountingIcon,
    },

    // finances
    // {
    //     category: ['finances'],
    //     title: {
    //         en: 'Business bank account',
    //         nl: 'Zakelijke bankrekening',
    //     },
    //     description: {
    //         en: 'Get a Dutch IBAN and virtual Mastercard ready to use within 3 business hours.',
    //         nl: 'Krijg binnen 3 uur een Dutch IBAN en virtuele Mastercard.',
    //     },
    //     price: { en: 0, nl: 0 },
    //     route: { name: 'client-banking' },
    //     icon: BankingIcon,
    // },

    // compliance
    {
        category: ['compliance'],
        title: {
            en: 'Shareholders agreement',
            nl: 'Aandeelhoudersovereenkomst',
        },
        description: {
            en: 'Record the relationships between shareholders in three steps, including agreements on shares, voting rights and profit distribution.',
            nl: 'Leg in drie stappen de verhoudingen tussen aandeelhouders vast, inclusief afspraken over aandelen, stemrecht en winstverdeling.',
        },
        product: ['aandeelhouderovereenkomst-opstellen'],
        route: { name: 'start-general-flow', query: { job: 'drafting-shareholders-agreement' } },
        icon: ContractsIcon,
    },
    {
        category: ['compliance'],
        title: {
            en: 'General terms and conditions',
            nl: 'Algemene voorwaarden',
        },
        description: {
            en: 'Protect your business with clearly defined terms and conditions. Prevent legal problems and provide certainty for you and your customers.',
            nl: 'Bescherm je bedrijf met duidelijk opgestelde voorwaarden. Voorkom juridische problemen en bied zekerheid voor jou en je klanten.',
        },
        price: { en: 1750, nl: 1750 },
        route: { name: 'start-general-flow', query: { job: 'drafting-terms-and-conditions' } },
        icon: ContractsIcon,
    },
    {
        category: ['compliance'],
        title: {
            en: 'Create management agreement',
            nl: 'Regel managementovereenkomst',
        },
        description: {
            en: 'Ensure 100% compliance and avoid issues with the tax authorities.',
            nl: 'Zorg dat je management fees voldoen aan de vereisten van de Belastingdienst.',
        },
        product: ['managementovereenkomst-opstellen'],
        route: { name: 'start-general-flow', query: { job: 'drafting-management-agreement' } },
        icon: ContractsIcon,
    },
    {
        category: ['compliance'],
        title: {
            en: 'Draft a founders agreement',
            nl: 'Founders agreement opstellen',
        },
        description: {
            en: 'Set clear agreements with your co-founders for now and the future.',
            nl: 'Leg duidelijke afspraken vast met je medeoprichters voor nu en de toekomst.',
        },
        price: { en: 1499, nl: 1499 },
        route: { name: 'start-general-flow', query: { job: 'drafting-shareholders-agreement' } },
        icon: ContractsIcon,
    },
    {
        category: ['compliance'],
        title: {
            en: 'UBO registration',
            nl: 'UBO registreren',
        },
        description: {
            en: 'Arrange it from your computer in just 10 minutes. A notary will then take care of your complete UBO registration.',
            nl: 'Vanuit huis je UBO registratie regelen. Snel en gemakkelijk online in 3 stappen.',
        },
        product: ['ubo-registratie'],
        route: { name: 'start-ubo-registration' },
        icon: ContractsIcon,
    },
    {
        category: ['compliance'],
        title: {
            en: 'Arrange your business legalization',
            nl: 'Regel je zakelijke legalisatie',
        },
        description: {
            en: 'Get your document officially certified and legalized for international use. Legalization makes your document valid abroad.',
            nl: 'In het geval van legalisatie wordt je document officieel verklaard, oftewel gelegaliseerd. Zo wordt je document ook bruikbaar voor het buitenland.',
        },
        product: ['legalisatie'],
        route: { name: 'start-general-flow', query: { job: 'legalization' } },
        icon: ContractsIcon,
    },
    {
        category: ['compliance'],
        title: {
            en: 'Articles of association',
            nl: 'Regel eenvoudig je naamswijziging',
        },
        description: {
            en: 'Arrange your articles of association amendment with our streamlined online process.',
            nl: 'Regel jouw statutenwijziging met ons gestroomlijnde online proces.',
        },
        product: ['naamswijziging'],
        route: { name: 'start-general-flow', query: { job: 'amendment-of-the-articles-of-association' } },
        icon: ModifyIcon,
    },
    {
        category: ['compliance'],
        title: {
            en: 'Capital reserve distribution',
            nl: 'Belastingvrij je agioreserve uitkeren',
        },
        description: {
            en: 'Arrange your share premium distribution with our streamlined online process.',
            nl: 'Regel jouw agioreserve uitkering met ons gestroomlijnde online proces.',
        },
        product: ['onbelast-uitkeren-agioreserve'],
        route: { name: 'start-general-flow', query: { job: 'tax-free-distribution-of-share-premium-reserve' } },
        icon: ContractsIcon,
    },
    {
        category: ['compliance'],
        title: {
            en: 'CV contract',
            nl: 'CV contract',
        },
        description: {
            en: 'Easily draft your CV (limited partnership) contract online. Includes legal consult and delivery.',
            nl: 'Stel eenvoudig online uw commanditaire vennootschap (CV)-overeenkomst op. Inclusief juridisch advies en levering.',
        },
        product: ['cv-contract-opstellen'],
        route: { name: 'start-general-flow', query: { job: 'drafting-cv-contract' } },
        icon: ContractsIcon,
    },
    {
        category: ['compliance'],
        title: {
            en: 'Register your trademark',
            nl: 'Registeer je merknaam',
        },
        description: {
            en: 'Register your trademark with our streamlined online process.',
            nl: 'Laat je merknaam registreren met ons gestroomlijnde online proces.',
        },
        product: ['merknaam-registratie'],
        route: { name: 'start-general-flow', query: { job: 'consultation-ip-advisor' } },
        icon: ContractsIcon,
    },
    {
        category: ['compliance'],
        title: {
            en: 'Have your agreement custom-tailored',
            nl: 'Laat je overeenkomst op maat opstellen',
        },
        description: {
            en: 'Need a contract that fits your specific needs? Have a qualified attorney draft a personalized legal agreement for your business.',
            nl: 'Heb je specifieke wensen voor je contract of overeenkomst? Laat een ervaren advocaat een maatwerk overeenkomst opstellen die past bij jouw situatie.',
        },
        product: ['verwerkersovereenkomst-nakijken-en-aanpassen'],
        route: { name: 'start-general-flow', query: { job: 'consultation-start-up-lawyer' } },
        icon: ContractsIcon,
    },

    // specialist
    {
        category: ['specialist'],
        title: {
            en: 'Consultation with a corporate law attorney',
            nl: 'Adviesgesprek met een advocaat ondernemingsrecht',
        },
        description: {
            en: 'Need legal support not listed in our services? Schedule a consultation with a corporate law expert.',
            nl: 'Heb je hulp nodig bij een ondernemingsrechtelijke kwestie? Plan een gesprek in met onze specialist.',
        },
        product: ['adviesgesprek-advocaat-ondernemingsrecht'],
        route: { name: 'start-general-flow', query: { job: 'consultation-lawyer-corporate-law' } },
        icon: ModifyIcon,
    },
    {
        category: ['specialist'],
        title: {
            en: 'Consultation with a bankruptcy law attorney',
            nl: 'Adviesgesprek met een advocaat faillisementsrecht',
        },
        description: {
            en: "Need help with a bankruptcy issue that's not listed in our services? Schedule a consultation with a legal expert.",
            nl: 'Heb je hulp nodig bij een faillissementskwestie? Plan een gesprek in met onze specialist.',
        },
        product: ['intakegesprek-advocaat-faillissementsrecht'],
        route: { name: 'start-general-flow', query: { job: 'consultation-lawyer-bankruptcy-law' } },
        icon: ModifyIcon,
    },
    {
        category: ['specialist'],
        title: {
            en: 'Consultation with a tax advisor',
            nl: 'Adviesgesprek met een belastingadviseur',
        },
        description: {
            en: 'For all tax questions of your company, it is wise to seek help from a tax advisor.',
            nl: 'Voor alle fiscale vragen van jouw bedrijf is het verstandig om hulp in te schakelen van een belastingadviseur.',
        },
        product: ['zakelijk-belastingadvies'],
        route: { name: 'start-general-flow', query: { job: 'consultation-tax-lawyer' } },
        icon: ModifyIcon,
    },
    {
        category: ['specialist'],
        title: {
            en: 'Consultation with a financing advisor',
            nl: 'Adviesgesprek met een financieringsadviseur',
        },
        description: {
            en: 'Book a one-hour session with our financing expert to get tailored advice for your funding challenge.',
            nl: 'Vraag een adviesgesprek aan van een uur met een van onze financieringsexperts om hun kennis te gebruiken voor jouw financieringsvraagstuk!',
        },
        product: ['adviesgesprek-financieringsadviseur'],
        route: { name: 'start-general-flow', query: { job: 'consultation-financial-advisor' } },
        icon: ModifyIcon,
    },
    {
        category: ['specialist'],
        title: {
            en: 'Consultation with a notary',
            nl: 'Adviesgesprek met een notaris',
        },
        description: {
            en: 'Need notarial advice for a specific legal question? Book a consultation with a notary.',
            nl: 'Heb je een specifieke vraag voor notarieel advies? Boek dan een gesprek met onze notaris.',
        },
        product: ['adviesgesprek-notaris'],
        route: { name: 'start-general-flow', query: { job: 'consultation-with-notary' } },
        icon: ModifyIcon,
    },
    {
        category: ['specialist'],
        title: {
            en: 'Consultation with a trademark specialist',
            nl: 'Adviesgesprek met een merkspecialist',
        },
        description: {
            en: 'Avoid the 5 biggest risks of doing business without trademark registration. Get expert advice from our specialists.',
            nl: 'Voorkom de 5 gevaren van ondernemen zonder merkregistratie en laat je goed adviseren door onze merkspecialisten.',
        },
        product: ['adviesgesprek-ie-adviseur'],
        route: { name: 'start-general-flow', query: { job: 'consultation-ip-advisor' } },
        icon: ModifyIcon,
    },
    {
        category: ['specialist'],
        title: {
            en: 'Consultation with a legal expert',
            nl: 'Adviesgesprek met een jurist',
        },
        description: {
            en: 'As a starting entrepreneur or SME, you need solid contracts without high legal costs. Get advice from our legal experts.',
            nl: 'Krijg juridisch advies van onze experts en laat toekomstbestendige contracten op maat opstellen.',
        },
        product: ['adviesgesprek-contract'],
        route: { name: 'start-general-flow', query: { job: 'consultation-contract' } },
        icon: ModifyIcon,
    },
];
